import React, { Suspense } from "react";
import { Spin } from "antd";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { CountyProvider } from "../context/CountyContext";
import { lazyWithReload as lazyRoute } from "../utils/lazyWithReload";

const PermitPlats = lazyRoute(() => import("../views/permitPlats/PermitPlats"));
const PermitPlat = lazyRoute(() => import("../views/permitPlats/PermitPlat"));
const Permits = lazyRoute(() => import("../views/permits/Permits"));
const Permit = lazyRoute(() => import("../views/permits/Permit"));
const Units = lazyRoute(() => import("../views/units/Units"));
const Owners = lazyRoute(() => import("../views/owners/Owners"));
const Interests = lazyRoute(() => import("../views/interests/Interests"));
const OwnerNameAddresses = lazyRoute(
  () => import("../views/owners/OwnerNameAddresses")
);
const OwnerNameAddressValidationClusters = lazyRoute(
  () => import("../views/owners/OwnerNameAddressValidationClusters")
);
const OwnerNameAddressValidationCluster = lazyRoute(
  () => import("../views/owners/OwnerNameAddressValidationCluster")
);
const FieldUnits = lazyRoute(() => import("../views/fieldUnits/FieldUnits"));
const FieldUnit = lazyRoute(() => import("../views/fieldUnits/FieldUnit"));

const Offers = lazyRoute(() => import("../views/offers/Offers"));
const Leads = lazyRoute(() => import("../views/leads/Leads"));
const DealReviews = lazyRoute(() => import("../views/dealReviews/DealReviews"));
const DealReview = lazyRoute(() => import("../views/dealReviews/DealReview"));
const Title = lazyRoute(() => import("../views/titles/Title"));
const Titles = lazyRoute(() => import("../views/titles/Titles"));
const Evaluations = lazyRoute(() => import("../views/evaluations/Evaluations"));
const Evaluation = lazyRoute(() => import("../views/evaluations/Evaluation"));
const Paperwork = lazyRoute(() => import("../views/paperworks/Paperwork"));

const MailExports = lazyRoute(() => import("../views/MailExports"));
const MailExportDetail = lazyRoute(() => import("../views/MailExportDetail"));
const MailLetter = lazyRoute(() => import("../views/MailLetter"));
const MailQueue = lazyRoute(() => import("../views/MailQueue"));
const PipedriveUploads = lazyRoute(
  () => import("../views/pipedriveUploads/PipedriveUploads")
);
const PipedriveUploadDetail = lazyRoute(
  () => import("../views/pipedriveUploads/PipedriveUploadDetail")
);
const WhitepagesBulkSearches = lazyRoute(
  () => import("../views/whitepagesBulkSearches/WhitepagesBulkSearches")
);
const WhitepagesBulkSearch = lazyRoute(
  () => import("../views/whitepagesBulkSearches/WhitepagesBulkSearch")
);
const WhitepagesSearches = lazyRoute(
  () => import("../views/whitepagesSearches/WhitepagesSearches")
);
const WhitepagesSearch = lazyRoute(
  () => import("../views/whitepagesSearches/WhitepagesSearch")
);
const WhitepagesPersons = lazyRoute(
  () => import("../views/whitepagesPersons/WhitepagesPersons")
);
const WhitepagesPerson = lazyRoute(
  () => import("../views/whitepagesPersons/WhitepagesPerson")
);

const RingCentralCallDetail = lazyRoute(
  () => import("../views/ringCentral/RingCentralCallDetail")
);
const Calls = lazyRoute(() => import("../views/calls/Calls"));
const CallDetail = lazyRoute(() => import("../views/calls/CallDetail"));
const Alerts = lazyRoute(() => import("../views/alerts/Alerts"));
const AlertDetail = lazyRoute(() => import("../views/alerts/Alert"));
const Activities = lazyRoute(() => import("../views/activities/Activities"));
const Activity = lazyRoute(() => import("../views/activities/Activity"));
const Conversations = lazyRoute(
  () => import("../views/conversations/Conversations")
);
const ConversationDetail = lazyRoute(
  () => import("../views/conversations/ConversationDetail")
);

const OwnerDetail = lazyRoute(() => import("../views/owners/OwnerDetail"));
const UnitDetail = lazyRoute(() => import("../views/units/UnitDetail"));
const UnitEvaluation = lazyRoute(
  () => import("../views/unitEvaluations/UnitEvaluation")
);
const TypeCurve = lazyRoute(() => import("../views/typeCurves/TypeCurve"));
const OfferDetail = lazyRoute(() => import("../views/offers/OfferDetail"));
const LeadDetail = lazyRoute(() => import("../views/leads/LeadDetail"));

const Entities = lazyRoute(() => import("../views/entities/Entities"));
const Entity = lazyRoute(() => import("../views/entities/Entity"));
const Assets = lazyRoute(() => import("../views/assets/Assets"));
const Asset = lazyRoute(() => import("../views/assets/Asset"));
const Tracts = lazyRoute(() => import("../views/tracts/Tracts"));
const Tract = lazyRoute(() => import("../views/tracts/Tract"));
const Deals = lazyRoute(() => import("../views/deals/Deals"));
const Deal = lazyRoute(() => import("../views/deals/Deal"));
const Tickets = lazyRoute(() => import("../views/tickets/Tickets"));
const Ticket = lazyRoute(() => import("../views/tickets/Ticket"));
const OperatorPayors = lazyRoute(
  () => import("../views/operatorPayors/OperatorPayors")
);
const OperatorPayor = lazyRoute(
  () => import("../views/operatorPayors/OperatorPayor")
);
const Operators = lazyRoute(() => import("../views/operators/Operators"));
const Operator = lazyRoute(() => import("../views/operators/Operator"));
const ManagementWells = lazyRoute(
  () => import("../views/wells/ManagementWells")
);
const ManagementWellRedirect = lazyRoute(
  () => import("../views/wells/ManagementWellRedirect")
);
const Well = lazyRoute(() => import("../views/wells/Well"));
const Royalties = lazyRoute(() => import("../views/royalties/Royalties"));
const Royalty = lazyRoute(() => import("../views/royalties/Royalty"));
const Sales = lazyRoute(() => import("../views/sales/Sales"));
const Sale = lazyRoute(() => import("../views/sales/Sale"));

const MetabaseResource = lazyRoute(
  () => import("../views/reports/MetabaseResource")
);

const Users = lazyRoute(() => import("../settings/Users"));
const User = lazyRoute(() => import("../settings/User"));

const Labels = lazyRoute(() => import("../settings/Labels"));
const AcquisitionsSettings = lazyRoute(
  () => import("../settings/AcquisitionsSettings")
);
const MailTemplates = lazyRoute(() => import("../settings/MailTemplates"));
const MailTemplate = lazyRoute(() => import("../settings/MailTemplate"));
const PipedriveSyncs = lazyRoute(() => import("../settings/PipedriveSyncs"));
const EnverusSyncs = lazyRoute(() => import("../settings/EnverusSyncs"));
const EnverusSync = lazyRoute(() => import("../settings/EnverusSync"));
const FileParses = lazyRoute(() => import("../settings/FileParses"));
const RRCSyncs = lazyRoute(() => import("../settings/RRCSyncs"));
const ForecastBacktests = lazyRoute(
  () => import("../settings/ForecastBacktests")
);
const ForecastBacktest = lazyRoute(
  () => import("../settings/ForecastBacktest")
);
const ParseBacktests = lazyRoute(() => import("../settings/ParseBacktests"));
const ParseBacktest = lazyRoute(() => import("../settings/ParseBacktest"));
const SONRISSyncs = lazyRoute(() => import("../settings/SONRISSyncs"));
const OwnerSyncs = lazyRoute(() => import("../settings/OwnerSyncs"));
const Jobs = lazyRoute(() => import("../settings/Jobs/Jobs"));
const Phones = lazyRoute(() => import("../settings/Phones"));

const IDISearches = lazyRoute(() => import("../views/idi/IDISearches"));
const IDISearch = lazyRoute(() => import("../views/idi/IDISearch"));
const IDIPersons = lazyRoute(() => import("../views/idi/IDIPersons"));
const IDIPerson = lazyRoute(() => import("../views/idi/IDIPerson"));
const IDIBulkSearches = lazyRoute(() => import("../views/IDIBulkSearches"));
const IDIBulkSearchDetail = lazyRoute(
  () => import("../views/IDIBulkSearchDetail")
);

const EndatoSearches = lazyRoute(
  () => import("../views/endato/EndatoSearches")
);
const EndatoSearch = lazyRoute(() => import("../views/endato/EndatoSearch"));
const EndatoPersons = lazyRoute(() => import("../views/endato/EndatoPersons"));
const EndatoPerson = lazyRoute(() => import("../views/endato/EndatoPerson"));

const USPSSearches = lazyRoute(() => import("../views/usps/USPSSearches"));
const USPSSearch = lazyRoute(() => import("../views/usps/USPSSearch"));
const USPSAddresses = lazyRoute(() => import("../views/usps/USPSAddresses"));
const USPSAddress = lazyRoute(() => import("../views/usps/USPSAddress"));
const USPSBulkSearches = lazyRoute(() => import("../views/USPSBulkSearches"));
const USPSBulkSearchDetail = lazyRoute(
  () => import("../views/USPSBulkSearchDetail")
);

const SimpleMap = lazyRoute(() => import("../views/maps/SimpleMap"));
const AreaOfInterests = lazyRoute(
  () => import("../views/areaOfInterests/AreaOfInterests")
);
const AreaOfInterest = lazyRoute(
  () => import("../views/areaOfInterests/AreaOfInterest")
);

const TaxingEntities = lazyRoute(
  () => import("../views/taxingEntities/TaxingEntities")
);
const TaxingEntity = lazyRoute(
  () => import("../views/taxingEntities/TaxingEntity")
);
const TaxStatements = lazyRoute(
  () => import("../views/taxStatements/TaxStatements")
);
const TaxStatement = lazyRoute(
  () => import("../views/taxStatements/TaxStatement")
);
const DivisionOrders = lazyRoute(
  () => import("../views/divisionOrders/DivisionOrders")
);
const DivisionOrder = lazyRoute(
  () => import("../views/divisionOrders/DivisionOrder")
);
const TaxAppraisals = lazyRoute(
  () => import("../views/taxAppraisals/TaxAppraisals")
);
const TaxAppraisal = lazyRoute(
  () => import("../views/taxAppraisals/TaxAppraisal")
);

const CallerIdReputations = lazyRoute(
  () => import("../views/callerIdReputations/CallerIdReputations")
);
const CallerIdReputation = lazyRoute(
  () => import("../views/callerIdReputations/CallerIdReputation")
);

const TexasTaxRollFiles = lazyRoute(
  () => import("../views/texasTaxRollFiles/TexasTaxRollFiles")
);
const TexasTaxRollFile = lazyRoute(
  () => import("../views/texasTaxRollFiles/TexasTaxRollFile")
);

const RouteFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
    <Spin size="large" />
  </div>
);

// Redirect old section paths to new ones (preserves sub-path)
const SectionRedirect = ({ from, to }) => {
  const location = useLocation();
  const newPath =
    location.pathname.replace(`/${from}`, `/${to}`) +
    location.search +
    location.hash;
  return <Navigate to={newPath} replace />;
};

const Router = (props) => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="permit-plats" element={<PermitPlats {...props} />} />
        <Route path="permit-plats/:id" element={<PermitPlat {...props} />} />
        <Route path="permits" element={<Permits {...props} />} />
        <Route path="permits/:id" element={<Permit {...props} />} />
        <Route path="units" element={<Units {...props} />} />
        <Route path="units/:id" element={<UnitDetail {...props} />} />
        <Route
          path="unit-evaluations/:id"
          element={<UnitEvaluation {...props} />}
        />
        <Route path="type-curves/:id" element={<TypeCurve {...props} />} />
        <Route path="owners" element={<Owners {...props} />} />
        <Route
          path="owners/:id"
          element={
            <CountyProvider>
              <OwnerDetail {...props} />
            </CountyProvider>
          }
        />
        <Route
          path="owner-name-addresses"
          element={<OwnerNameAddresses {...props} />}
        />
        <Route
          path="owner-name-address-validation-clusters"
          element={<OwnerNameAddressValidationClusters {...props} />}
        />
        <Route
          path="owner-name-address-validation-clusters/:id"
          element={<OwnerNameAddressValidationCluster {...props} />}
        />
        <Route path="field-units" element={<FieldUnits {...props} />} />
        <Route path="field-units/:id" element={<FieldUnit {...props} />} />
        <Route path="interests" element={<Interests {...props} />} />
        <Route path="offers" element={<Offers {...props} />} />
        <Route path="offers/:id" element={<OfferDetail {...props} />} />
        <Route
          path="pipedrive-uploads"
          element={<PipedriveUploads {...props} />}
        />
        <Route
          path="pipedrive-uploads/:id"
          element={<PipedriveUploadDetail {...props} />}
        />
        <Route
          path="whitepages-bulk-searches"
          element={<WhitepagesBulkSearches {...props} />}
        />
        <Route
          path="whitepages-bulk-searches/:id"
          element={<WhitepagesBulkSearch {...props} />}
        />
        <Route
          path="whitepages-searches"
          element={<WhitepagesSearches {...props} />}
        />
        <Route
          path="whitepages-searches/:id"
          element={<WhitepagesSearch {...props} />}
        />
        <Route
          path="whitepages-persons"
          element={<WhitepagesPersons {...props} />}
        />
        <Route
          path="whitepages-persons/:id"
          element={<WhitepagesPerson {...props} />}
        />
        <Route
          path="ringcentral-calls/:id"
          element={<RingCentralCallDetail {...props} />}
        />
        <Route path="calls" element={<Calls {...props} />} />
        <Route path="calls/:id" element={<CallDetail {...props} />} />
        <Route path="conversations" element={<Conversations {...props} />} />
        <Route
          path="conversations/:id"
          element={<ConversationDetail {...props} />}
        />
        <Route path="mail-exports" element={<MailExports {...props} />} />
        <Route
          path="mail-exports/:id"
          element={<MailExportDetail {...props} />}
        />
        <Route path="mail-letters/:id" element={<MailLetter {...props} />} />
        <Route path="mail-queue" element={<MailQueue {...props} />} />
        <Route path="leads" element={<Leads {...props} />} />
        <Route path="leads/:id" element={<LeadDetail {...props} />} />
        <Route path="deal-reviews" element={<DealReviews {...props} />} />
        <Route path="deal-reviews/:id" element={<DealReview {...props} />} />
        <Route path="titles" element={<Titles {...props} />} />
        <Route path="titles/:id" element={<Title {...props} />} />
        <Route path="evaluations" element={<Evaluations {...props} />} />
        <Route
          path="evaluations/:id"
          element={
            <CountyProvider>
              <Evaluation {...props} />
            </CountyProvider>
          }
        />
        <Route path="paperworks/:id" element={<Paperwork {...props} />} />
        <Route path="phones" element={<Phones {...props} />} />
        <Route path="idi-searches" element={<IDISearches {...props} />} />
        <Route path="idi-searches/:id" element={<IDISearch {...props} />} />
        <Route path="idi-persons" element={<IDIPersons {...props} />} />
        <Route path="idi-persons/:id" element={<IDIPerson {...props} />} />
        <Route path="activities" element={<Activities {...props} />} />
        <Route path="activities/:id" element={<Activity {...props} />} />
        <Route
          path="idi-bulk-searches"
          element={<IDIBulkSearches {...props} />}
        />
        <Route
          path="idi-bulk-searches/:id"
          element={<IDIBulkSearchDetail {...props} />}
        />
        <Route path="endato-searches" element={<EndatoSearches {...props} />} />
        <Route
          path="endato-searches/:id"
          element={<EndatoSearch {...props} />}
        />
        <Route path="endato-persons" element={<EndatoPersons {...props} />} />
        <Route
          path="endato-persons/:id"
          element={<EndatoPerson {...props} />}
        />
        <Route path="usps-searches" element={<USPSSearches {...props} />} />
        <Route path="usps-searches/:id" element={<USPSSearch {...props} />} />
        <Route path="usps-addresses" element={<USPSAddresses {...props} />} />
        <Route path="usps-addresses/:id" element={<USPSAddress {...props} />} />
        <Route
          path="usps-bulk-searches"
          element={<USPSBulkSearches {...props} />}
        />
        <Route
          path="usps-bulk-searches/:id"
          element={<USPSBulkSearchDetail {...props} />}
        />
        <Route
          path="acquisitions/reports/main"
          element={
            <MetabaseResource
              {...props}
              resourceType="dashboard"
              resourceId={3}
              href="https://metabase.taborminerals.com/dashboard/3-crm"
              title="Acquisitions Reports"
            />
          }
        />
        <Route
          path="acquisitions/mailer-checks"
          element={
            <MetabaseResource
              {...props}
              resourceType="dashboard"
              resourceId={7}
              href={`https://metabase.taborminerals.com/dashboard/7-mailer-checks`}
              title="Mailer Checks"
            />
          }
        />
        <Route path="map" element={<SimpleMap {...props} />} />
        <Route
          path="area-of-interests"
          element={<AreaOfInterests {...props} />}
        />
        <Route
          path="area-of-interests/:id"
          element={<AreaOfInterest {...props} />}
        />
        <Route path="management/entities" element={<Entities {...props} />} />
        <Route path="management/entities/:id" element={<Entity {...props} />} />
        <Route path="management/assets" element={<Assets {...props} />} />
        <Route path="management/assets/:id" element={<Asset {...props} />} />
        <Route path="management/tracts" element={<Tracts {...props} />} />
        <Route path="management/tracts/:id" element={<Tract {...props} />} />
        <Route path="management/deals" element={<Deals {...props} />} />
        <Route path="management/deals/:id" element={<Deal {...props} />} />
        <Route path="management/tickets" element={<Tickets {...props} />} />
        <Route path="management/tickets/:id" element={<Ticket {...props} />} />
        <Route
          path="management/operator-payors"
          element={<OperatorPayors {...props} />}
        />
        <Route
          path="management/operator-payors/:id"
          element={<OperatorPayor {...props} />}
        />
        <Route path="management/operators" element={<Operators {...props} />} />
        <Route
          path="management/operators/:id"
          element={<Operator {...props} />}
        />
        <Route
          path="management/wells"
          element={<ManagementWells {...props} />}
        />
        <Route
          path="management/wells/:id"
          element={<ManagementWellRedirect />}
        />
        <Route path="management/royalties" element={<Royalties {...props} />} />
        <Route
          path="management/royalties/:id"
          element={<Royalty {...props} />}
        />
        <Route
          path="management/division-orders"
          element={<DivisionOrders {...props} />}
        />
        <Route
          path="management/division-orders/:id"
          element={<DivisionOrder {...props} />}
        />
        <Route
          path="management/taxing-entities"
          element={<TaxingEntities {...props} />}
        />
        <Route
          path="management/taxing-entities/:id"
          element={<TaxingEntity {...props} />}
        />
        <Route
          path="management/tax-statements"
          element={<TaxStatements {...props} />}
        />
        <Route
          path="management/tax-statements/:id"
          element={<TaxStatement {...props} />}
        />
        <Route
          path="management/tax-appraisals"
          element={<TaxAppraisals {...props} />}
        />
        <Route
          path="management/tax-appraisals/:id"
          element={<TaxAppraisal {...props} />}
        />
        <Route path="management/sales" element={<Sales {...props} />} />
        <Route path="management/sales/:id" element={<Sale {...props} />} />
        <Route path="management/alerts" element={<Alerts {...props} />} />
        <Route
          path="management/alerts/:id"
          element={<AlertDetail {...props} />}
        />
        <Route
          path="management/reports/main"
          element={
            <MetabaseResource
              {...props}
              resourceType="dashboard"
              resourceId={2}
              href="https://metabase.taborminerals.com/dashboard/2-portfolio"
              title="Portfolio Reports"
            />
          }
        />
        <Route path="settings/users" element={<Users {...props} />} />
        <Route path="settings/users/:id" element={<User {...props} />} />
        <Route path="settings/labels" element={<Labels {...props} />} />
        <Route
          path="settings/acquisitions"
          element={<AcquisitionsSettings {...props} />}
        />
        <Route
          path="settings/mail-templates"
          element={<MailTemplates {...props} />}
        />
        <Route
          path="settings/mail-templates/:id"
          element={<MailTemplate {...props} />}
        />
        <Route
          path="settings/pipedrive-syncs"
          element={<PipedriveSyncs {...props} />}
        />
        <Route
          path="settings/enverus-syncs"
          element={<EnverusSyncs {...props} />}
        />
        <Route
          path="settings/enverus-syncs/:id"
          element={<EnverusSync {...props} />}
        />
        <Route path="settings/rrc-syncs" element={<RRCSyncs {...props} />} />
        <Route
          path="settings/forecast-backtests"
          element={<ForecastBacktests {...props} />}
        />
        <Route
          path="settings/forecast-backtests/:id"
          element={<ForecastBacktest {...props} />}
        />
        <Route
          path="settings/parse-backtests"
          element={<ParseBacktests {...props} />}
        />
        <Route
          path="settings/parse-backtests/:id"
          element={<ParseBacktest {...props} />}
        />
        <Route
          path="settings/sonris-syncs"
          element={<SONRISSyncs {...props} />}
        />
        <Route
          path="settings/owner-syncs"
          element={<OwnerSyncs {...props} />}
        />
        <Route
          path="settings/file-parses"
          element={<FileParses {...props} />}
        />
        <Route path="settings/jobs" element={<Jobs {...props} />} />
        <Route
          path="caller-id-reputations"
          element={<CallerIdReputations {...props} />}
        />
        <Route
          path="caller-id-reputations/:id"
          element={<CallerIdReputation {...props} />}
        />
        <Route
          path="texas-tax-roll-files"
          element={<TexasTaxRollFiles {...props} />}
        />
        <Route
          path="texas-tax-roll-files/:id"
          element={<TexasTaxRollFile {...props} />}
        />
        <Route path="wells/:id" element={<Well {...props} />} />

        {/* Redirects from old section paths */}
        <Route
          path="portfolio/*"
          element={<SectionRedirect from="portfolio" to="management" />}
        />
        <Route
          path="crm/*"
          element={<SectionRedirect from="crm" to="acquisitions" />}
        />
        <Route
          path="settings/portfolio"
          element={<Navigate to="/settings/acquisitions" replace />}
        />
        <Route
          path="settings/crm"
          element={<Navigate to="/settings/acquisitions" replace />}
        />
      </Routes>
    </Suspense>
  );
};
export default Router;
